import clsx from 'clsx';
import { createRef, useState } from 'react';
import Slider from 'react-slick';

import EditableField from '~components/generic/editable-field/EditableField/EditableField';
import AddLink from '~components/generic/elements/add-link/AddLink';
import DraggableModuleContainer from '~components/generic/module/DraggableModuleContainer';
import { IEntityComponentProps } from '~interfaces/props';
import { ModuleSliderNextArrow } from '~modules/slider/ModuleSliderNextArrow';
import { ModuleSliderPrevArrow } from '~modules/slider/ModuleSliderPrevArrow';
import { getRelatedEntityIds } from '~services/entity-helpers';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import cssStyles from './ModuleSliderStyles.css';

export default function ModuleSlider(props: IEntityComponentProps) {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const slideRef = createRef<Slider>();

  function handleSlideChange(i: number) {
    if (!slideRef.current) return;
    slideRef.current.slickGoTo(i);
    setActiveSlideIndex(i);
  }

  const { data, canEdit, canMove, editing, className, onChange, onAddNewChild, origData, stable, isEditing } = props;
  if (!data) {
    return null;
  }
  const content = data.attributes;
  const moduleClasses = clsx({ ...className.module }, 'c-app__outside-content', {
    [cssStyles.movable]: canMove,
  });
  const childModuleIds = getRelatedEntityIds(data, 'field_items');
  const childModuleIdsLength = childModuleIds.length - 1;
  const isSlidable = !isEditing || canEdit;
  const hasMultipleSlides = childModuleIds.length > 1;

  const fieldDurationValue: number = parseInt(data.attributes.field_duration, 10) || 0;
  const enableAutoplay = fieldDurationValue > 0;

  return (
    <div className={moduleClasses} id={getModuleUrlIdFromData(data.id, data.type)}>
      <div className="c-app__inside-content u-auto-width c-slider-menu">
        <div className="container px-0 c-slider-menu__inner relative">
          <EditableField
            hide={content.field_hide_title}
            entityType={data.type}
            fieldName="attributes.field_title.value"
            data={content.field_title.value}
            editing={editing}
            onChange={onChange}
          />
        </div>
      </div>
      <Slider
        arrows={isSlidable}
        dots={false}
        accessibility={isSlidable}
        draggable={isSlidable}
        className="c-slider u-margin-bottom-xs js-slider c-module__content"
        afterChange={handleSlideChange}
        ref={slideRef}
        nextArrow={<ModuleSliderNextArrow />}
        prevArrow={<ModuleSliderPrevArrow />}
        infinite={isSlidable && hasMultipleSlides}
        autoplay={isSlidable && enableAutoplay && hasMultipleSlides}
        autoplaySpeed={fieldDurationValue}
      >
        {childModuleIds.map((id, i) => {
          return (
            <div className="c-slider__slide c-slider__slide--with-settings relative" key={id}>
              <DraggableModuleContainer
                parentIsStable={stable}
                index={i}
                entityId={id}
                parentContext={data.id}
                parentType={data.type}
                fieldName="relationships.field_items.data"
                siblingsLength={childModuleIdsLength}
                canMove={editing}
              />
            </div>
          );
        })}
      </Slider>
      <div className="c-app__inside-content u-auto-width c-slider-dots js-slider-dots">
        <div className="container px-0 c-slider-dots__inner js-slider-dots__inner">
          <ul>
            {childModuleIds.map((module, i) => {
              const isActiveSlide = i === activeSlideIndex;
              return (
                <li role="presentation" key={i}>
                  <button
                    type="button"
                    role="tab"
                    aria-label={`${i} of ${childModuleIdsLength}`}
                    aria-selected={isActiveSlide}
                    onClick={() => handleSlideChange(i)}
                  />
                </li>
              );
            })}
          </ul>
        </div>
        {editing && (
          <div className="container px-0">
            <AddLink
              onClick={() =>
                onAddNewChild({
                  id: origData,
                  fieldName: 'relationships.field_items.data',
                  templateType: 'paragraph--slider_item',
                })
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
